import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { CARRIER_BRIDGE_LABEL } from "kontrolEdge/helpers/carrierBridgeName";

/**
 * Carrier Bridge rows of a port's Associated Connections (CTX-947).
 *
 * The port-connections endpoint sends them as `carrierBridge`, next to the
 * other service types. They live here, in the module, so the shared
 * `PortConnections` only registers them like its siblings.
 */

/** The row `type` the table switches on to link each row to its detail. */
export const CARRIER_BRIDGE_CONNECTION_TYPE = CARRIER_BRIDGE_LABEL;

/** One `carrierBridge` entry of `GET /kontroledge/ports/:id/connections`. */
export interface PortCarrierBridge {
  id: number;
  name?: string | null;
  subscriptionId?: string | null;
  mbps?: number | null;
  /** null = the whole port goes to the carrier. */
  vlan?: string | number | null;
  carrier?: { id: number; name: string; code: string | null } | null;
  term?: number | null;
  term_unit?: string | null;
}

interface PortWithCarrierBridges {
  carrierBridge?: PortCarrierBridge[] | null;
}

/**
 * The port's Carrier Bridge services in the shape the table flattens every
 * connection to: the service plus its `type` and the port as `parent`.
 */
export const carrierBridgePortConnections = <P extends PortWithCarrierBridges>(
  port: P | null | undefined
): Array<PortCarrierBridge & { type: string; parent: P }> => {
  if (!port || !Array.isArray(port.carrierBridge)) return [];
  return port.carrierBridge.map((service) => ({
    ...service,
    type: CARRIER_BRIDGE_CONNECTION_TYPE,
    parent: port,
  }));
};

/**
 * Detail of a Carrier Bridge row. Like its DCI and Cloud siblings in this
 * table it carries no tenant: the detail falls back to the selection, which
 * is the port's own customer here.
 */
export const carrierBridgeConnectionPath = (carrierBridgeId: number | string): string =>
  buildDetailPath("carrierBridge", { carrierBridgeId });
